import { createResource } from "~/lib/resourceFactory";
import {
  getAccount,
  getAccountBalanceHistory,
  listAccounts,
  listAllAccounts,
} from "./account.api";
import type { AccountBalanceHistoryPeriod } from "./account.server";

type AccountsPage = Awaited<ReturnType<typeof listAccounts>>;
type AccountDetail = Awaited<ReturnType<typeof getAccount>>;

export type AccountDetailParams = { businessId: string; id: string };

export type AccountsParams = {
  businessId: string;
  search?: string;
  offset?: number;
  limit: number;
  types?: string[];
  classIds?: string[];
};

// A chart of accounts changes rarely, so a long staleTime lets cached data
// survive navigation and window focus without refetching (see provider.tsx
// for the global refetchOnWindowFocus:false). This was a top source of
// repeated listAccounts DB calls.
const ACCOUNTS_STALE_TIME = 5 * 60_000; // 5 min

export const AccountDetailResource = createResource<
  AccountDetail,
  AccountDetailParams
>({
  key: ({ businessId, id }) => ["account", businessId, id] as const,
  fetcher: ({ businessId, id }) => getAccount({ data: { businessId, id } }),
  staleTime: ACCOUNTS_STALE_TIME,
});

export type AccountsAllParams = { businessId: string };
type AccountsAll = Awaited<ReturnType<typeof listAllAccounts>>;

export const AccountsAllResource = createResource<
  AccountsAll,
  AccountsAllParams
>({
  key: (p) => ["account", p.businessId, "all"] as const,
  fetcher: (p) => listAllAccounts({ data: { businessId: p.businessId } }),
  staleTime: ACCOUNTS_STALE_TIME,
});

export const AccountsResource = createResource<AccountsPage, AccountsParams>({
  key: (p) =>
    [
      "account",
      p.businessId,
      p.limit,
      p.search ?? "",
      p.offset ?? 0,
      ...(p.types ?? []).sort(),
      ...(p.classIds ?? []).sort(),
    ] as const,
  fetcher: (p) =>
    listAccounts({
      data: {
        businessId: p.businessId,
        search: p.search,
        offset: p.offset,
        limit: p.limit,
        types: p.types,
        classIds: p.classIds,
      },
    }),
  staleTime: ACCOUNTS_STALE_TIME,
});

export type AccountBalanceHistoryParams = {
  businessId: string;
  accountId: string;
  period: AccountBalanceHistoryPeriod;
};

type AccountBalanceHistory = Awaited<
  ReturnType<typeof getAccountBalanceHistory>
>;

export const AccountBalanceHistoryResource = createResource<
  AccountBalanceHistory,
  AccountBalanceHistoryParams
>({
  key: (p) =>
    [
      "account",
      p.businessId,
      p.accountId,
      "balance-history",
      p.period,
    ] as const,
  fetcher: (p) =>
    getAccountBalanceHistory({
      data: {
        businessId: p.businessId,
        accountId: p.accountId,
        period: p.period,
      },
    }),
  // Embedded chart on the account detail page. The route loader prefetches the
  // "monthly" period (fire-and-forget, not awaited) so the chart resolves from
  // cache on client navigation. placeholderData is still required: during SSR
  // the prefetch may not have completed, and without placeholderData the
  // enabled createQuery would suspend the nearest Suspense boundary (hard-
  // refresh freeze / slow first paint). Rendering from an empty placeholder
  // keeps SSR + client first render in sync; real data updates the chart.
  placeholderData: { accountId: "", period: "monthly", points: [] },
});
